import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import i18next from "i18next";
import { toast } from "react-toastify";
import type { CustomFont, CustomFontFace } from "src/types";

import type { RootState } from "@/redux/root-reducer";
import endpoints from "@/utils/endpoints";
import { authenticatedFetch } from "@/utils/fetchJSON";
import { isError } from "@/utils/is-error";

import { getToken } from "../authentication/authentication-reducer";
import { getState } from "../helpers";
import { getIsFirstLoading } from "../requests/requests-reducer";

const get = endpoints();

export const fetchCustomFonts = createAsyncThunk<CustomFont[], string>(
  "customFonts/fetch",
  async (slug: string, { rejectWithValue }) => {
    const response = await authenticatedFetch.fetchJSON<CustomFont[]>(
      get("CUSTOM_FONTS_API")(slug)
    );

    if (isError(response)) {
      toast.error(response.description, { toastId: response.description });
      return rejectWithValue(response);
    }

    return response;
  }
);

export const createFont = createAsyncThunk<
  CustomFont,
  {
    slug: string;
    name: string;
  }
>("customFonts/createFont", async (params, { rejectWithValue }) => {
  const response = await authenticatedFetch.fetchJSON<CustomFont>(
    get("CUSTOM_FONTS_API")(params.slug),
    {
      method: "POST",
      body: {
        font: {
          name: params.name,
        },
      },
    }
  );

  if (isError(response)) {
    toast.error(response.description, { toastId: response.description });
    return rejectWithValue(response);
  }

  toast.success(i18next.t("preference-panel.custom-font-created"));
  return response;
});

export const createFontFace = createAsyncThunk<
  CustomFontFace,
  { slug: string; fontId: string; style: string; fontFace: File }
>("customFont/createFontFace", async (params, { rejectWithValue }) => {
  const formData = new FormData();
  formData.append("font_face[style]", params.style);
  formData.append("font_face[file]", params.fontFace);

  const response = await authenticatedFetch.fetchJSON<CustomFontFace>(
    get("CUSTOM_FONT_FACE_API")(params.slug, params.fontId),
    {
      method: "POST",
      body: formData,
    }
  );

  if (isError(response)) {
    toast.error(response.description, { toastId: response.description });
    return rejectWithValue(response);
  }

  toast.success(i18next.t("preference-panel.custom-font-uploaded"));
  return response;
});

export const deleteFontFace = createAsyncThunk<
  void,
  {
    slug: string;
    fontId: string;
    fontFaceId: string;
  }
>(
  "customFont/deleteFontFace",
  async (
    params: {
      slug: string;
      fontId: string;
      fontFaceId: string;
    },
    { rejectWithValue, getState }
  ) => {
    const token = getToken(getState() as RootState);

    try {
      // This endpoint returns a 204 with no content.
      // Using fetchJSON will not work here.
      await fetch(
        get("DELETE_CUSTOM_FONT_FACE_API")(
          params.slug,
          params.fontId,
          params.fontFaceId
        ),
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success(i18next.t("preference-panel.custom-font-removed"));
    } catch (error) {
      toast.error(error?.message || i18next.t("common.error-response.general"));
      return rejectWithValue(null);
    }
  }
);

export const customFontsAdapter = createEntityAdapter<CustomFont>();

const customFontsSlice = createSlice({
  name: "customFonts",
  initialState: customFontsAdapter.getInitialState(),
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchCustomFonts.fulfilled, customFontsAdapter.upsertMany);
    builder.addCase(createFont.fulfilled, customFontsAdapter.addOne);
    builder.addCase(createFontFace.fulfilled, (state, action) => {
      const faces = state.entities[action.meta.arg.fontId].font_faces;

      const index = faces.findIndex(
        (face) => face.style === action.payload.style
      );

      if (index > -1) {
        state.entities[action.meta.arg.fontId].font_faces[index] =
          action.payload;
      } else {
        state.entities[action.meta.arg.fontId].font_faces.push(action.payload);
      }

      return state;
    });
    builder.addCase(deleteFontFace.fulfilled, (state, action) => {
      const faces = state.entities[action.meta.arg.fontId].font_faces;

      const index = faces.findIndex(
        (face) => face.id === action.meta.arg.fontFaceId
      );

      if (index > -1) {
        const faces = state.entities[action.meta.arg.fontId].font_faces;
        state.entities[action.meta.arg.fontId].font_faces = [
          ...faces.slice(0, index),
          ...faces.slice(index + 1),
        ];
        return state;
      }
    });
  },
});

const {
  selectAll: getAllCustomFonts,
  selectById: getByCustomFontId,
} = customFontsAdapter.getSelectors((state: RootState) => state.customFonts);

export { getAllCustomFonts, getByCustomFontId };

export const getIsCustomFontsLoading = createSelector(getState, (state) => {
  return getIsFirstLoading(state, fetchCustomFonts);
});

export default customFontsSlice.reducer;
